import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { StyleSheet, Pressable, View, Platform } from 'react-native';
import { useTheme } from '@/context/ThemeContext';
import Text from '@/components/Typography/Text';
import Button from '@/components/Buttons/Button';
import ThemeBlock from '@/components/ThemeBlock';
import { Themes, ACCENT_COLORS, ACCENT_TEXT_COLORS, ThemeNames } from '@/constants/Colors';
import { useUIStore, useAuthStore, useModalStore, useToastStore } from '@/store';
import { accountApi } from '@/lib/api';
import * as DropdownMenu from '@/components/DropdownMenu/DropdownMenu';
import { useTranslation } from '@/hooks/useTranslation';
import SettingsSection from '@/components/SettingsSection';
import useResponsiveness from '@/hooks/useResponsiveness';
import DropdownMenuCheckboxItem from '@/components/DropdownMenu/DropdownMenuCheckboxItem';
import SwitchInput from '@/components/Inputs/Switch';
import { MESSAGE_LAYOUT, MESSAGE_WIDTH, CHAT_INPUT_STYLE } from '@/constants/config';
import Tabs from '@/components/Tabs';
import Tooltip from '@/components/Tooltip';
import { capitalizeFirstLetter } from '@/constants/utils/text';
import { getPetById } from '@/constants/pets';

const createStyles = (colors, isXS) => StyleSheet.create({
  section: {
    paddingVertical: 12,
    borderTopColor: colors.border_color_secondary,
    borderTopWidth: 0.5,
  },
  firstSection: {
    borderTopWidth: 0,
    paddingTop: 0,
    flexDirection: 'column',
    borderBottomWidth: 1,
    borderBottomColor: colors.border_color_secondary,
  },
  sectionRow: {
    flexDirection: 'row',
    alignItems: 'center',
    flexWrap: 'wrap',
    gap: 16,
    justifyContent: 'space-between',
  },
  themeOptions: {
    flexShrink: 1,
    marginTop: 15,
    flexDirection: 'column',
    gap: 0,
  },
  themeRow: {
    flexDirection: 'row',
    flexWrap: 'wrap',
    alignItems: 'flex-start',
    gap: 0,
  },
  colorGrid: {
    flexDirection: 'row',
    flexWrap: 'wrap',
    gap: isXS ? 12 : 8,
    justifyContent: isXS ? 'flex-start' : 'flex-end',
    paddingBottom: 15,
  },
  colorOption: {
    width: isXS ? 24 : 16,
    height: isXS ? 24 : 16,
    borderRadius: isXS ? 12 : 8,
    borderWidth: 0.5,
    borderColor: colors.border_color_secondary,
  },
  colorOptionText: {
    marginTop: 4,
    textTransform: 'capitalize',
    position: 'absolute',
    bottom: -20,
  },
  colorOptionActive: {
    borderWidth: 2,
    borderColor: colors.solid_color_primary,
  },
  colorOptionActiveMacOS: {
    borderWidth: 2,
    borderColor: '#1a87ff',
  },
  toggle: {
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'space-between',
    gap: 16,
  },
  toggleTextContainer: {
    flex: 1,
    paddingRight: 16,
  },
  menuBarSetting: {
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'space-between',
  },
  tabsContainer: {
    flexDirection: 'column',
    width: '100%',
    marginBottom: 8,
  },
});

function AppearanceSettings() {
  const { t } = useTranslation();
  const { isXS } = useResponsiveness();
  const { colors, theme, setTheme, accentBackgroundColor, setAccentBackgroundColor, accentTextColor, setAccentTextColor } = useTheme();
  const messageLayout = useUIStore(state => state.messageLayout);
  const messageWidth = useUIStore(state => state.messageWidth);
  const setMessageLayout = useUIStore(state => state.setMessageLayout);
  const setMessageWidth = useUIStore(state => state.setMessageWidth);
  const tagsLayout = useUIStore(state => state.tagsLayout);
  const setTagsLayout = useUIStore(state => state.setTagsLayout);
  const increaseBorderContrast = useUIStore(state => state.increaseBorderContrast);
  const setIncreaseBorderContrast = useUIStore(state => state.setIncreaseBorderContrast);
  // const showTagCounts = useUIStore(state => state.showTagCounts);
  // const setShowTagCounts = useUIStore(state => state.setShowTagCounts);
  const showTimestamps = useUIStore(state => state.showTimestamps);
  const setShowTimestamps = useUIStore(state => state.setShowTimestamps);
  const showMessageActions = useUIStore(state => state.showMessageActions);
  const setShowMessageActions = useUIStore(state => state.setShowMessageActions);
  const showSourcesOutputsPanel = useUIStore(state => state.showSourcesOutputsPanel !== false);
  const setShowSourcesOutputsPanel = useUIStore(state => state.setShowSourcesOutputsPanel);
  const chatInputStyle = useUIStore(state => state.chatInputStyle);
  const setChatInputStyle = useUIStore(state => state.setChatInputStyle);
  const enterToSend = useUIStore(state => state.enterToSend);
  const setEnterToSend = useUIStore(state => state.setEnterToSend);
  const keepScreenAwake = useUIStore(state => state.keepScreenAwakeInChats === true);
  const setKeepScreenAwake = useUIStore(state => state.setKeepScreenAwakeInChats);
  const [activeTab, setActiveTab] = useState('style');

  // Follow-up suggestions toggle is a user-level (DB-backed) preference so
  // it follows the account across devices and the backend can skip the
  // extra inference call entirely when the user has opted out.
  const userChatPreferences = useAuthStore(state => state.user?.preferences?.chat);
  const updateUserPreferences = useAuthStore(state => state.updateUserPreferences);
  const selectedPetId = useAuthStore(state => state.user?.preferences?.appearance?.selectedPet || null);
  const { openModal } = useModalStore(state => state.actions);
  const { addToast } = useToastStore(state => state.actions);
  const [showFollowUpSuggestions, setShowFollowUpSuggestions] = useState(
    userChatPreferences?.followUpSuggestionsEnabled !== false
  );
  const [isUpdatingFollowUps, setIsUpdatingFollowUps] = useState(false);
  const [showCollectionSuggestions, setShowCollectionSuggestions] = useState(
    userChatPreferences?.collectionSuggestionsEnabled !== false
  );
  const [isUpdatingCollectionSuggestions, setIsUpdatingCollectionSuggestions] = useState(false);
  const [showStockCharts, setShowStockCharts] = useState(
    userChatPreferences?.stockChartsEnabled !== false
  );
  const [isUpdatingStockCharts, setIsUpdatingStockCharts] = useState(false);
  const [showWorkModeSuggestions, setShowWorkModeSuggestions] = useState(
    userChatPreferences?.workModeSuggestionsEnabled !== false
  );
  const [isUpdatingWorkModeSuggestions, setIsUpdatingWorkModeSuggestions] = useState(false);

  useEffect(() => {
    setShowFollowUpSuggestions(userChatPreferences?.followUpSuggestionsEnabled !== false);
  }, [userChatPreferences?.followUpSuggestionsEnabled]);

  useEffect(() => {
    setShowCollectionSuggestions(userChatPreferences?.collectionSuggestionsEnabled !== false);
  }, [userChatPreferences?.collectionSuggestionsEnabled]);

  useEffect(() => {
    setShowStockCharts(userChatPreferences?.stockChartsEnabled !== false);
  }, [userChatPreferences?.stockChartsEnabled]);

  useEffect(() => {
    setShowWorkModeSuggestions(userChatPreferences?.workModeSuggestionsEnabled !== false);
  }, [userChatPreferences?.workModeSuggestionsEnabled]);

  const handleFollowUpSuggestionsToggle = useCallback(async (value) => {
    if (isUpdatingFollowUps) return;
    const previous = showFollowUpSuggestions;
    setShowFollowUpSuggestions(value);
    setIsUpdatingFollowUps(true);
    try {
      await accountApi.updateChatPreferences({ followUpSuggestionsEnabled: value });
      updateUserPreferences({ chat: { followUpSuggestionsEnabled: value } });
      addToast(
        value
          ? t('settings.appearance.showFollowUpSuggestions.enabled')
          : t('settings.appearance.showFollowUpSuggestions.disabled'),
        'success'
      );
    } catch (error) {
      console.error('Error updating follow-up suggestions preference:', error);
      setShowFollowUpSuggestions(previous);
      addToast(t('settings.appearance.showFollowUpSuggestions.updateFailed'), 'error');
    } finally {
      setIsUpdatingFollowUps(false);
    }
  }, [isUpdatingFollowUps, showFollowUpSuggestions, updateUserPreferences, addToast, t]);

  const handleCollectionSuggestionsToggle = useCallback(async (value) => {
    if (isUpdatingCollectionSuggestions) return;
    const previous = showCollectionSuggestions;
    setShowCollectionSuggestions(value);
    setIsUpdatingCollectionSuggestions(true);
    try {
      await accountApi.updateChatPreferences({ collectionSuggestionsEnabled: value });
      updateUserPreferences({ chat: { collectionSuggestionsEnabled: value } });
      addToast(
        value
          ? t('settings.appearance.showCollectionSuggestions.enabled')
          : t('settings.appearance.showCollectionSuggestions.disabled'),
        'success'
      );
    } catch (error) {
      console.error('Error updating collection suggestions preference:', error);
      setShowCollectionSuggestions(previous);
      addToast(t('settings.appearance.showCollectionSuggestions.updateFailed'), 'error');
    } finally {
      setIsUpdatingCollectionSuggestions(false);
    }
  }, [isUpdatingCollectionSuggestions, showCollectionSuggestions, updateUserPreferences, addToast, t]);

  const handleEnterToSendToggle = useCallback(async (value) => {
    const next = value === true;
    setEnterToSend(next);
    if (!useAuthStore.getState().user?.id) return;
    try {
      await accountApi.updateChatPreferences({ enterToSend: next });
      updateUserPreferences({ chat: { enterToSend: next } });
    } catch (error) {
      console.error('Error updating enter-to-send preference:', error);
    }
  }, [setEnterToSend, updateUserPreferences]);

  const handleWorkModeSuggestionsToggle = useCallback(async (value) => {
    if (isUpdatingWorkModeSuggestions) return;
    const previous = showWorkModeSuggestions;
    setShowWorkModeSuggestions(value);
    setIsUpdatingWorkModeSuggestions(true);
    try {
      await accountApi.updateChatPreferences({ workModeSuggestionsEnabled: value });
      updateUserPreferences({ chat: { workModeSuggestionsEnabled: value } });
      addToast(
        value
          ? t('settings.appearance.showWorkModeSuggestions.enabled')
          : t('settings.appearance.showWorkModeSuggestions.disabled'),
        'success'
      );
    } catch (error) {
      console.error('Error updating work mode suggestions preference:', error);
      setShowWorkModeSuggestions(previous);
      addToast(t('settings.appearance.showWorkModeSuggestions.updateFailed'), 'error');
    } finally {
      setIsUpdatingWorkModeSuggestions(false);
    }
  }, [isUpdatingWorkModeSuggestions, showWorkModeSuggestions, updateUserPreferences, addToast, t]);

  const handleStockChartsToggle = useCallback(async (value) => {
    if (isUpdatingStockCharts) return;
    const previous = showStockCharts;
    setShowStockCharts(value);
    setIsUpdatingStockCharts(true);
    try {
      await accountApi.updateChatPreferences({ stockChartsEnabled: value });
      updateUserPreferences({ chat: { stockChartsEnabled: value } });
      addToast(
        value
          ? t('settings.appearance.showStockCharts.enabled')
          : t('settings.appearance.showStockCharts.disabled'),
        'success'
      );
    } catch (error) {
      console.error('Error updating stock charts preference:', error);
      setShowStockCharts(previous);
      addToast(t('settings.appearance.showStockCharts.updateFailed'), 'error');
    } finally {
      setIsUpdatingStockCharts(false);
    }
  }, [isUpdatingStockCharts, showStockCharts, updateUserPreferences, addToast, t]);

  const styles = useMemo(() => createStyles(colors, isXS), [colors, isXS]);
  const selectedPet = useMemo(() => getPetById(selectedPetId), [selectedPetId]);

  const handleMessageLayoutSelect = (value) => {
    setMessageLayout(value);
  };

  const handleTagsLayoutSelect = (value) => {
    setTagsLayout(value);
  };

  const handleChatInputStyleSelect = (value) => {
    setChatInputStyle(value);
  };

  return (
    <View>
      <View style={[styles.section, styles.firstSection]}>
        <Text style={{ width: 150 }}>
          {t('settings.appearance.theme')}
          <Text size='n' color='tertiary'>
            {` - ${capitalizeFirstLetter(theme)}`}
          </Text>
        </Text>
        <View style={styles.themeOptions}>
          {/* Light themes row */}
          <View style={styles.themeRow}>
            {ThemeNames.filter(themeName => ['white', 'ocean', 'mint', 'pastel', 'paper'].includes(themeName)).map(themeName => (
              <ThemeBlock
                key={themeName}
                title={t(`themes.${themeName}`)}
                value={themeName}
                isActive={theme === themeName}
                onPress={setTheme}
                colors={Themes[themeName]}
              />
            ))}
            {ThemeNames.filter(themeName => ['sepia', 'muted', 'night', 'smoke', 'black'].includes(themeName)).map(themeName => (
              <ThemeBlock
                key={themeName}
                title={t(`themes.${themeName}`)}
                value={themeName}
                isActive={theme === themeName}
                onPress={setTheme}
                colors={Themes[themeName]}
              />
            ))}
          </View>
        </View>
      </View>

      <SettingsSection
        title={t('settings.appearance.accentBackgroundColor')}
        titleNote={capitalizeFirstLetter(accentBackgroundColor)}
        isColumn={isXS}
      >
        <View style={styles.colorGrid}>
          {ACCENT_COLORS.map(({ id, color }) => (
            <View key={id} style={{ alignItems: 'center' }}>
              <Tooltip label={capitalizeFirstLetter(id.toLowerCase())}>
                <Pressable
                  accessibilityRole="button"
                  accessibilityLabel={`${t('settings.appearance.accentBackgroundColor')}: ${capitalizeFirstLetter(id)}`}
                  accessibilityState={{ selected: accentBackgroundColor === id }}
                  onPress={() => setAccentBackgroundColor(id.toLowerCase())}
                  style={[
                    styles.colorOption,
                    accentBackgroundColor === id && (Platform.OS === 'macos' ? styles.colorOptionActiveMacOS : styles.colorOptionActive),
                    { backgroundColor: color },
                    id === 'multicolor' && { backgroundColor: colors.accent_color_primary },
                    (id === 'multicolor' || id === 'white') && { borderWidth: 1, borderColor: colors.border_color_secondary }
                  ]}
                />
              </Tooltip>
              {accentBackgroundColor === id && Platform.OS === 'web' && (
                <Text color='secondary' size='xxs' style={styles.colorOptionText}>
                  {id}
                </Text>
              )}
            </View>
          ))}
        </View>
      </SettingsSection>

      <SettingsSection
        title={t('settings.appearance.accentTextColor')}
        titleNote={Platform.OS === 'macos' ? capitalizeFirstLetter(accentTextColor) : undefined}
        isColumn={isXS}
      >
        <View style={styles.colorGrid}>
          {ACCENT_TEXT_COLORS.map(({ id, color }) => (
            <View key={id} style={{ alignItems: 'center' }}>
              <Tooltip label={capitalizeFirstLetter(id.toLowerCase())}>
                <Pressable
                  accessibilityRole="button"
                  accessibilityLabel={`${t('settings.appearance.accentTextColor')}: ${capitalizeFirstLetter(id)}`}
                  accessibilityState={{ selected: accentTextColor === id }}
                  onPress={() => setAccentTextColor(id.toLowerCase())}
                  style={[
                    styles.colorOption,
                    accentTextColor === id && (Platform.OS === 'macos' ? styles.colorOptionActiveMacOS : styles.colorOptionActive),
                    { backgroundColor: color },
                    (id === 'white' || id === 'beige' || id === 'cream') && { 
                      borderWidth: 1,
                      borderColor: colors.border_color_secondary 
                    }
                  ]}
                />
              </Tooltip>
              {accentTextColor === id && Platform.OS !== 'macos' && (
                <Text color='secondary' size='xxs' style={styles.colorOptionText}>
                  {id}
                </Text>
              )}
            </View>
          ))}
        </View>
      </SettingsSection>

      <SettingsSection title={t('settings.appearance.messageLayout.title')}>
        <View style={styles.menuBarSetting}>
          <DropdownMenu.Root>
            <DropdownMenu.Trigger>
              <Button
                text={`${t(`settings.appearance.messageLayout.${messageLayout.toLowerCase()}`)} • ${t(`settings.appearance.messageWidth.${messageWidth.toLowerCase()}`)}`}
                variant="secondary"
                size="small"
                appendIcon="chevron-down"
                appendIconSize={12}
              />
            </DropdownMenu.Trigger>
            <DropdownMenu.Content className="DropdownMenuContent" style={{ minWidth: 200 }}>
              <View style={styles.tabsContainer}>
                <Tabs
                  size='s'
                  tabs={[
                    {
                      id: 'style',
                      label: t('settings.appearance.style'),
                      active: activeTab === 'style',
                      onPress: () => setActiveTab('style')
                    },
                    {
                      id: 'width',
                      label: t('settings.appearance.width'),
                      active: activeTab === 'width',
                      onPress: () => setActiveTab('width')
                    }
                  ]}
                />
              </View>

              {activeTab === 'style' && Object.values(MESSAGE_LAYOUT).map(layout => (
                <DropdownMenuCheckboxItem
                  key={layout}
                  onValueChange={() => setMessageLayout(layout.toLowerCase())}
                  isActive={messageLayout?.toLowerCase() === layout.toLowerCase()}
                  label={t(`settings.appearance.messageLayout.${layout.toLowerCase()}`)}
                />
              ))}

              {activeTab === 'width' && Object.values(MESSAGE_WIDTH).map(width => (
                <DropdownMenuCheckboxItem
                  key={width}
                  onValueChange={() => setMessageWidth(width.toLowerCase())}
                  isActive={messageWidth?.toLowerCase() === width.toLowerCase()}
                  label={t(`settings.appearance.messageWidth.${width.toLowerCase()}`)}
                />
              ))}
            </DropdownMenu.Content>
          </DropdownMenu.Root>
        </View>
      </SettingsSection>

      <SettingsSection title={t('settings.appearance.chatInputStyle.title')}>
        <View style={styles.menuBarSetting}>
          <DropdownMenu.Root>
            <DropdownMenu.Trigger>
              <Button
                text={t(`settings.appearance.chatInputStyle.${chatInputStyle.toLowerCase()}`)}
                variant="secondary"
                size="small"
                appendIcon="chevron-down"
                appendIconSize={12}
              />
            </DropdownMenu.Trigger>
            <DropdownMenu.Content className="DropdownMenuContent">
              {Object.values(CHAT_INPUT_STYLE).map(style => (
                <DropdownMenuCheckboxItem
                  key={style}
                  onValueChange={() => setChatInputStyle(style.toLowerCase())}
                  isActive={chatInputStyle?.toLowerCase() === style.toLowerCase()}
                  label={t(`settings.appearance.chatInputStyle.${style.toLowerCase()}`)}
                />
              ))}
            </DropdownMenu.Content>
          </DropdownMenu.Root>
        </View>
      </SettingsSection>

      {(Platform.OS === 'web' || Platform.OS === 'macos') && (
        <SettingsSection
          title={t('pets.settingsTitle')}
          subtitle={t('pets.settingsSubtitle')}
        >
          <View style={styles.menuBarSetting}>
            <Button
              text={selectedPet ? selectedPet.name : t('pets.none')}
              variant="secondary"
              size="small"
              appendIcon="chevron-right"
              appendIconSize={12}
              onPress={() => openModal('PetPickerModal')}
            />
          </View>
        </SettingsSection>
      )}

      {/* <SettingsSection title={t('settings.appearance.tagsLayout.title')}>
        <View style={styles.menuBarSetting}>
          <DropdownMenu.Root>
            <DropdownMenu.Trigger>
              <Button
                text={t(`settings.appearance.tagsLayout.${tagsLayout.toLowerCase()}`)}
                variant="secondary"
                size="small"
                appendIcon="chevron-down"
                appendIconSize={12}
              />
            </DropdownMenu.Trigger>
            <DropdownMenu.Content className="DropdownMenuContent">
              {['List', 'Grid'].map(layout => (
                <DropdownMenuCheckboxItem
                  key={layout}
                  onValueChange={() => setTagsLayout(layout.toLowerCase())}
                  isActive={tagsLayout?.toLowerCase() === layout.toLowerCase()}
                  label={t(`settings.appearance.tagsLayout.${layout.toLowerCase()}`)}
                />
              ))}
            </DropdownMenu.Content>
          </DropdownMenu.Root>
        </View>
      </SettingsSection> */}

      <SettingsSection 
        title={t('settings.appearance.showTimestamps.title')}
        subtitle={t('settings.appearance.showTimestamps.subtitle')}
      >
        <View style={styles.menuBarSetting}>
          <SwitchInput
              accessibilityLabel={t('settings.appearance.showTimestamps.title')}
            value={showTimestamps}
            onValueChange={setShowTimestamps}
          />
        </View>
      </SettingsSection>

      <SettingsSection 
        title={t('settings.appearance.showMessageActions.title')}
        subtitle={t('settings.appearance.showMessageActions.subtitle')}
      >
        <View style={styles.menuBarSetting}>
          <SwitchInput
              accessibilityLabel={t('settings.appearance.showMessageActions.title')}
            value={showMessageActions}
            onValueChange={setShowMessageActions}
          />
        </View>
      </SettingsSection>

      {(Platform.OS === 'ios' || Platform.OS === 'android') && (
        <SettingsSection
          title={t('settings.appearance.keepScreenAwake.title')}
          subtitle={t('settings.appearance.keepScreenAwake.subtitle')}
        >
          <View style={styles.menuBarSetting}>
            <SwitchInput
              accessibilityLabel={t('settings.appearance.keepScreenAwake.title')}
              value={keepScreenAwake}
              onValueChange={setKeepScreenAwake}
            />
          </View>
        </SettingsSection>
      )}

      {Platform.OS === 'web' && !isXS && (
        <SettingsSection
          title={t('settings.appearance.showSourcesOutputsPanel.title')}
          subtitle={t('settings.appearance.showSourcesOutputsPanel.subtitle')}
        >
          <View style={styles.menuBarSetting}>
            <SwitchInput
              accessibilityLabel={t('settings.appearance.showSourcesOutputsPanel.title')}
              value={showSourcesOutputsPanel}
              onValueChange={setShowSourcesOutputsPanel}
            />
          </View>
        </SettingsSection>
      )}

      <SettingsSection
        title={t('settings.appearance.showFollowUpSuggestions.title')}
        subtitle={t('settings.appearance.showFollowUpSuggestions.subtitle')}
      >
        <View style={styles.menuBarSetting}>
          <SwitchInput
              accessibilityLabel={t('settings.appearance.showFollowUpSuggestions.title')}
            value={showFollowUpSuggestions}
            onValueChange={handleFollowUpSuggestionsToggle}
          />
        </View>
      </SettingsSection>

      <SettingsSection
        title={t('settings.appearance.showCollectionSuggestions.title')}
        subtitle={t('settings.appearance.showCollectionSuggestions.subtitle')}
      >
        <View style={styles.menuBarSetting}>
          <SwitchInput
              accessibilityLabel={t('settings.appearance.showCollectionSuggestions.title')}
            value={showCollectionSuggestions}
            onValueChange={handleCollectionSuggestionsToggle}
          />
        </View>
      </SettingsSection>

      <SettingsSection
        title={t('settings.appearance.showWorkModeSuggestions.title')}
        subtitle={t('settings.appearance.showWorkModeSuggestions.subtitle')}
      >
        <View style={styles.menuBarSetting}>
          <SwitchInput
              accessibilityLabel={t('settings.appearance.showWorkModeSuggestions.title')}
            value={showWorkModeSuggestions}
            onValueChange={handleWorkModeSuggestionsToggle}
          />
        </View>
      </SettingsSection>

      <SettingsSection
        title={t('settings.appearance.showStockCharts.title')}
        subtitle={t('settings.appearance.showStockCharts.subtitle')}
      >
        <View style={styles.menuBarSetting}>
          <SwitchInput
              accessibilityLabel={t('settings.appearance.showStockCharts.title')}
            value={showStockCharts}
            onValueChange={handleStockChartsToggle}
          />
        </View>
      </SettingsSection>

      {!isXS && (
        <SettingsSection 
          title={t('settings.appearance.enterToSend.title')}
          subtitle={t('settings.appearance.enterToSend.subtitle')}
        >
          <View style={styles.menuBarSetting}>
            <SwitchInput
              accessibilityLabel={t('settings.appearance.enterToSend.title')}
              value={enterToSend !== false}
              onValueChange={handleEnterToSendToggle}
            />
          </View>
        </SettingsSection>
      )}

      <SettingsSection 
        isLast
        title={t('settings.appearance.increaseBorderContrast.title')}
        subtitle={t('settings.appearance.increaseBorderContrast.subtitle')}
      >
        <View style={styles.menuBarSetting}>
          <SwitchInput
              accessibilityLabel={t('settings.appearance.increaseBorderContrast.title')}
            value={increaseBorderContrast}
            onValueChange={setIncreaseBorderContrast}
          />
        </View>
      </SettingsSection>

      {/* <SettingsSection 
        title={t('settings.appearance.showTagCounts.title')}
        subtitle={t('settings.appearance.showTagCounts.subtitle')}
      >
        <View style={styles.menuBarSetting}>
          <SwitchInput
              accessibilityLabel={t('settings.appearance.showTagCounts.title')}
            value={showTagCounts}
            onValueChange={setShowTagCounts}
          />
        </View>
      </SettingsSection> */}
    </View>
  );
} 

export default React.memo(AppearanceSettings);
